import { useIntl } from '@weenat/wintl'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Text from 'app/src/kit/Text'
import { countryCodeEmoji } from 'country-code-emoji'
import * as i18nCountries from 'i18n-iso-countries'
import { styled } from 'styled-components'
import Modal from '../Modal'

export const allowedCountryCodes = [
  'FR',
  'ES',
  'IT',
  'CH',
  'BE',
  'GB',
  'PT',
  'DE',
  'NL',
  'AF',
  'AX',
  'AL',
  'DZ',
  'AS',
  'AD',
  'AO',
  'AI',
  'AQ',
  'AG',
  'AR',
  'AM',
  'AW',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BZ',
  'BJ',
  'BM',
  'BT',
  'BO',
  'BQ',
  'BA',
  'BW',
  'BV',
  'BR',
  'IO',
  'BN',
  'BG',
  'BF',
  'BI',
  'CV',
  'KH',
  'CM',
  'CA',
  'KY',
  'CF',
  'TD',
  'CL',
  'CN',
  'CX',
  'CC',
  'CO',
  'KM',
  'CG',
  'CD',
  'CK',
  'CR',
  'CI',
  'HR',
  'CU',
  'CW',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'SZ',
  'ET',
  'FK',
  'FO',
  'FJ',
  'FI',
  'GF',
  'PF',
  'TF',
  'GA',
  'GM',
  'GE',
  'GH',
  'GI',
  'GR',
  'GL',
  'GD',
  'GP',
  'GU',
  'GT',
  'GG',
  'GN',
  'GW',
  'GY',
  'HT',
  'HM',
  'VA',
  'HN',
  'HK',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IM',
  'IL',
  'JM',
  'JP',
  'JE',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MO',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MQ',
  'MR',
  'MU',
  'YT',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MS',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NC',
  'NZ',
  'NI',
  'NE',
  'NG',
  'NU',
  'NF',
  'KP',
  'MK',
  'MP',
  'NO',
  'OM',
  'PK',
  'PW',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PN',
  'PL',
  'PR',
  'QA',
  'RE',
  'RO',
  'RU',
  'RW',
  'BL',
  'SH',
  'KN',
  'LC',
  'MF',
  'PM',
  'VC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SX',
  'SK',
  'SI',
  'SB',
  'SO',
  'ZA',
  'GS',
  'KR',
  'SS',
  'LK',
  'SD',
  'SR',
  'SJ',
  'SE',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TK',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TC',
  'TV',
  'UG',
  'UA',
  'AE',
  'UM',
  'US',
  'UY',
  'UZ',
  'VU',
  'VE',
  'VN',
  'VG',
  'VI',
  'WF',
  'EH',
  'YE',
  'ZM',
  'ZW'
] as const

const CurrentCountry = styled(Box)`
  border: none;
  margin: 0;
  padding-right: 12px;
  background-color: transparent;
  cursor: pointer;
`

const CountryListItem = styled.button<{ $isSelected: boolean }>`
  padding: ${(p) => p.theme.spacings.md}px;
  border: none;
  margin: none;
  width: 100%;
  background-color: ${(p) => (p.$isSelected ? p.theme.colors.grayscale[100] : 'transparent')};
  display: flex;
  border-bottom: 1px solid ${(p) => p.theme.colors.grayscale[100]};
  cursor: pointer;
`

type CountryCode = (typeof allowedCountryCodes)[number]

export interface CountryPickerProps {
  value: CountryCode
  onChange: (newSelection: { cca2: CountryCode }) => void
}

const CountryPicker: FC<CountryPickerProps> = ({ value, onChange }) => {
  const { locales } = useIntl()
  const { isOpen, close, open } = useDisclosure()

  return (
    <>
      <CurrentCountry onClick={open}>{countryCodeEmoji(value)}</CurrentCountry>
      <Modal close={close} isOpen={isOpen}>
        {allowedCountryCodes.map((countryCode) => (
          <CountryListItem
            $isSelected={countryCode === value}
            key={countryCode}
            onClick={() => {
              onChange({ cca2: countryCode })
              close()
            }}
          >
            <Text>{countryCodeEmoji(countryCode)}</Text>
            <Box $ml='lg'>
              <Text>{i18nCountries.getName(countryCode, locales.current)}</Text>
            </Box>
          </CountryListItem>
        ))}
      </Modal>
    </>
  )
}

export default CountryPicker
